import { INPUT_TYPES } from 'app-constants/inputTypes';
import { createSpecParser, createParseProdJson, nextTransitionIdFor } from './specParser/core';
import { fetchGlobalVariables } from './globalVariables';
import { fetchGlobalFunctions } from './globalFunctions';
import slugify from './utils';

// Parser del spec 2.0, offline (lo ejecuta twnel-cf-api-botcore-edge). La lógica común
// está en specParser/core.js; aquí solo lo propio de esta versión.

const titleKey = (title) => title;
const nextTransitionId = nextTransitionIdFor(titleKey);

const parseGenericInputType = (property) => ({
  ...property,
  method: 'keyboard',
  data: {
    placeholder: property?.data?.placeholder,
  },
});

const parseNumberInputType = (property) => {
  const {
    placeholder,
    integer,
    min,
    max,
  } = property?.data || {};
  return {
    ...property,
    method: 'keyboard',
    data: {
      placeholder,
      integer,
      min,
      max,
    },
  };
};

const parseRadioCheckboxInputType = (property) => {
  let data;

  if (property.optionsVariable && property.optionsVariable.length > 0) {
    // Verificar si la variable ya tiene el prefijo session. o global.
    const hasSessionPrefix = property.optionsVariable.startsWith('session.');
    const hasGlobalPrefix = property.optionsVariable.startsWith('global.');

    if (hasSessionPrefix || hasGlobalPrefix) {
      // Ya tiene el prefijo correcto, usarla tal cual
      data = `{{ variables.${property.optionsVariable} | to_json_str }}`;
    } else {
      // No tiene prefijo, agregar session. por defecto
      data = `{{ variables.session.${property.optionsVariable} | to_json_str }}`;
    }
  } else {
    data = Object.keys(property.data).reduce(
      (accum, key) => [
        ...accum, { id: property.data[key].value, label: property.data[key].name },
      ], [],
    );
  }

  // El esquema de validación para radio/checkbox no permite 'method'
  // (tiene additionalProperties: false y solo permite type y data)
  return {
    type: property.type,
    data,
  };
};

const parseDateInputType = (property) => {
  const date = new Date(property.data.default);
  // El motor offline (botcore-edge) exige day/month/year cuando `default` existe: "usar la fecha
  // actual" (useDefaultDate) se expresa omitiendo `default`, no con `{}` (el bot fallaba con
  // "Input schema for step … does not match").
  const hasDefault = !property.data.useDefaultDate && !Number.isNaN(date.getTime());
  return {
    type: 'date',
    data: {
      format: property.data.format,
      placeholder: property.data.format,
      ...(hasDefault && {
        default: { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() },
      }),
    },
  };
};

const parsePasswordLocationInputType = (property) => ({
  ...property,
  method: 'keyboard',
  data: Object.keys(property.data).reduce(
    (accum, key) => ({ ...accum, [key]: property.data[key] }),
    {},
  ),
});

const parseMediaInputType = (property) => ({
  type: property.type,
  geotag: property.data?.geotag !== undefined ? property.data.geotag : false,
  data: {
    media_type: property?.data?.media_type,
    picker: property?.data?.picker,
    facing: property.data.camera,
  },
});

const parseBarcodeInputType = (property) => {
  /** @type {Record<string, any>} */
  const data = {
    type: 'barcode',
    data: { response: property.data.response },
    geotag: property.data?.geotag !== undefined ? property.data.geotag : false,
  };
  if (property.data.subtype) {
    data.subtype = property.data.subtype;
  }
  return data;
};

const parseSignatureInputType = (property) => ({
  type: 'signature',
  data: { caption: property.data.caption },
  geotag: property.data?.geotag !== undefined ? property.data.geotag : false,
});

const parseInputType = (property) => {
  const parseInputType = {
    [INPUT_TYPES.TEXT]: parseGenericInputType,
    [INPUT_TYPES.NUMBER]: parseNumberInputType,
    [INPUT_TYPES.PHONE]: parseGenericInputType,
    [INPUT_TYPES.EMAIL]: parseGenericInputType,
    [INPUT_TYPES.RADIO]: parseRadioCheckboxInputType,
    [INPUT_TYPES.CHECKBOX]: parseRadioCheckboxInputType,
    [INPUT_TYPES.DATE]: parseDateInputType,
    [INPUT_TYPES.PASSWORD]: parsePasswordLocationInputType,
    [INPUT_TYPES.LOCATION]: parsePasswordLocationInputType,
    [INPUT_TYPES.MEDIA]: parseMediaInputType,
    [INPUT_TYPES.BARCODE]: parseBarcodeInputType,
    [INPUT_TYPES.SIGNATURE]: parseSignatureInputType,
  };

  const newProperty = { ...property };
  delete newProperty.varType;
  // El motor offline (twnel-cf-api-botcore-edge) valida `input` con additionalProperties: false y
  // ningún tipo admite `timeout` (es un concepto del motor online v1): con él, el bot falla en el
  // dispositivo con "Input schema for step … does not match".
  delete newProperty.timeout;
  return parseInputType[property.type](newProperty);
};

const parseCallApiAction = (properties, nodeId, nodes) => {
  const HTTP_OK = 200;
  const DEFAULT_SCHEMA = {};

  const params = Object.keys(properties.params).reduce((accum, key) => {
    const values = [...Object.values(properties.params[key])];
    return { ...accum, [values[0]]: values[1] };
  }, {});

  const headers = Object.keys(properties.headers).reduce((accum, key) => {
    const values = [...Object.values(properties.headers[key])];
    return { ...accum, [values[0]]: values[1] };
  }, {});

  const { connections } = nodes[nodeId].outputs.on_error;
  let onErrorNextTransition = '';

  if (connections.length > 0) {
    onErrorNextTransition = nextTransitionId(nodes, connections);
  }

  const {
    type, vars, method, url, body, postVerify, contentType,
    timeout,
  } = properties;

  const status = postVerify.status ? postVerify.status.map((code) => parseInt(code.split(' ')[0], 10)) : HTTP_OK;

  return {
    type,
    vars,
    eval: {
      method: method.toUpperCase(),
      content_type: contentType,
      url,
      params,
      headers,
      ...body.length > 2 && { body },
      timeout: parseInt(timeout || '0', 10),
    },
    post_eval: {
      verify: {
        ...contentType === 'application/json' ? { schema: postVerify.schema ? JSON.parse(postVerify.schema) : DEFAULT_SCHEMA } : {},
        status,
        timecap: parseInt(postVerify.timecap || '0', 10),
      },
    },
    ...onErrorNextTransition.length > 0 && { on_error: { next: onErrorNextTransition } },
  };
};

const addToJsonString = (value, variables) => {
  const response = {};
  Object.keys(value).forEach((key) => {
    const objectValue = value[key];
    if (objectValue.includes('}}')) {
      // objectValue is {{ variables.session.check }}, I need variables.session.check
      const vars = objectValue.replace('}}', '').trim().split('.');
      const varName = vars[vars.length - 1];
      // Puede no ser una variable conocida (global no cargada, transition.*): se deja igual.
      const varType = variables[varName]?.type;
      if (varType === 'object' || varType === 'array') {
        response[key] = objectValue.replace('}}', '| to_json_str }}');
      } else {
        response[key] = objectValue;
      }
    } else {
      response[key] = objectValue;
    }
  });
  return response;
};

/**
 * Genera un hash numérico simple de una cadena para usarlo como clave única de caché.
 * No criptográfico: solo necesitamos que sea estable y sin colisiones para URLs razonables.
 */
const hashUrl = (url) => {
  let hash = 0;
  for (let i = 0; i < url.length; i += 1) {
     
    hash = (Math.imul(31, hash) + url.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(16);
};

/**
 * Detecta URLs de imágenes en strings.
 * Cubre dos casos:
 *  - URLs con extensión de imagen explícita (.jpg, .png, etc.)
 *  - URLs de CDNs conocidos que sirven imágenes sin extensión:
 *    Google Drive thumbnails, Cloudinary, imgix, etc.
 */
const IMAGE_URL_PATTERN = new RegExp(
  [
    // URLs con extensión de imagen al final (con query opcional)
    'https?://[^\\s"\']+\\.(?:jpg|jpeg|png|gif|webp|svg)(?:\\?[^\\s"\']*)?',
    // Google Drive thumbnail
    'https?://drive\\.google\\.com/thumbnail\\?[^\\s"\']+',
    // Cloudinary, imgix y similares (host conocido + /image/)
    'https?://(?:res\\.cloudinary\\.com|[^\\s"\']+\\.imgix\\.net)/[^\\s"\']+',
  ].join('|'),
  'gi',
);

/**
 * Detecta URLs de imágenes en un valor arbitrario (string, array, object) de forma recursiva.
 * Diseñado para encontrar URLs embebidas en variables de sesión (ej: arrays de ejercicios,
 * listas de recursos visuales, etc.).
 */
const extractImageUrlsFromValue = (value, seen) => {
  const found = [];

  if (typeof value === 'string') {
    // Caso 1: URL directa o URL embebida en un string más largo
    if (!value.includes('{{')) {
      const matches = value.match(IMAGE_URL_PATTERN) || [];
      matches.forEach((url) => {
        if (!seen.has(url)) {
          seen.add(url);
          found.push(url);
        }
      });
    }
  } else if (Array.isArray(value)) {
    value.forEach((item) => {
      found.push(...extractImageUrlsFromValue(item, seen));
    });
  } else if (value !== null && typeof value === 'object') {
    Object.values(value).forEach((v) => {
      found.push(...extractImageUrlsFromValue(v, seen));
    });
  }

  return found;
};

/**
 * Escanea las fuentes de media estática del spec y genera el manifest offline_media.
 *
 * Fuentes escaneadas:
 *  1. Acciones send_message con media_url estática (no Jinja).
 *  2. Variables de sesión cuyo value contiene URLs de imagen (arrays de recursos,
 *     links de ejercicios, etc.).
 *
 * Formato de cada entrada:
 *   { url: "https://...", key: "offline_<hash>", type: "image" }
 *
 * Solo se incluyen URLs estáticas. Las que contienen "{{" dependen de variables
 * en runtime y no se pueden pre-descargar.
 */
const collectOfflineMedia = (actions, specJson) => {
  const seen = new Set();
  const result = [];

  // ── Fuente 1: send_message.media_url ──────────────────────────────────────
  Object.values(actions).forEach((action) => {
    if (action.type === 'send_message' && action.media_url) {
      const { media_url: url } = action;
      if (typeof url === 'string' && !url.includes('{{') && !seen.has(url)) {
        seen.add(url);
        result.push({ url, key: `offline_${hashUrl(url)}`, type: 'image' });
      }
    }
  });

  // ── Fuente 2: variables de sesión con URLs de imagen en sus values ─────────
  // Cubre casos como preparatorias_array, compensatorias_array, etc., donde
  // el builder embebe arrays de [titulo, url_imagen, descripcion].
  const sessionVars = specJson?.variables?.session || {};
  Object.entries(sessionVars).forEach(([, varDef]) => {
    if (!varDef || varDef.value === undefined) return;
    const urls = extractImageUrlsFromValue(varDef.value, seen);
    urls.forEach((url) => {
      result.push({ url, key: `offline_${hashUrl(url)}`, type: 'image' });
    });
  });

  return result;
};

const parser = createSpecParser({
  titleKey,
  rulesTitle: undefined,
  rulesActionTitle: slugify,
  parseInputType,
  parseCallApiAction,
  callFunctionInput: addToJsonString,
  mapResultFrom: (path) => `{{ ${path} | to_json_str }}`,
  radioOptionValue: (option) => option.id,
  implicitConditionalStep: {
    // El output de radio tiene answer.selected.id, no answer.id.
    transitionId: (title, nodeId) => `transition.${slugify(slugify(title) + nodeId)}.output.answer.selected.id`,
    actionKey: (nodeId) => `conditional_step_implicit_${nodeId}_action`,
  },
  // Manifest de medios offline: el cliente Android los pre-descarga al sincronizar el bot.
  // Vacío si no hay media_url estáticas en ninguna acción send_message.
  finalize: (spec, { actions, specJson }) => ({
    ...spec,
    offline_media: collectOfflineMedia(actions, specJson),
  }),
});

export const {
  parseNodeTransitions: parseNodeTransitionsV2,
  parseNodeActions: parseNodeActionsV2,
} = parser;
const { parseNodes: parseNodesV2 } = parser;

export const parseProdJsonV2 = createParseProdJson(
  parseNodesV2, [fetchGlobalVariables, fetchGlobalFunctions],
);

export default parseNodesV2;
